// src/components/LandingPage.jsx

import { Link } from "react-router-dom";
import { motion} from "framer-motion";
import Header from "../components/Header";
import { blogPosts } from "../data/blogPosts";
import HowItWorks from "./HowItWorks";



export default function LandingPage() {
  

  // fade-up variant for sections
  const fadeUp = {
    hidden: { opacity: 0, y: 50 },
    show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  const features = [
    { title: "Weather-Aware Lists", desc: "Auto-tailored packing based on live forecasts." },
    { title: "Traveler Profiles", desc: "Save preferences for every traveler." },
    { title: "Family Mode", desc: "Pack smarter for all ages." },
    { title: "Trip Extras", desc: "Add formalwear, gym gear, and more." },
  ];

  return (
    <div className="overflow-x-hidden text-gray-900 bg-white">
      <Header />
      {/* 1️⃣ Hero */}
      <section 
        id = "hero"
        className="pt-[100px] flex flex-col items-center justify-center min-h-[90vh] px-6 text-center">
        
        <motion.h1
          className="pb-[100px] text-5xl sm:text-6xl font-bold leading-tight mb-4 bg-gradient-to-r from-[#6B91B5] to-[#EE4A72] bg-clip-text text-transparent"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
        >
          Travel smarter, pack lighter.
        </motion.h1>
        <motion.p
          className="max-w-xl text-lg text-gray-600 mb-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
        >
          SmartPackr creates your perfect packing list — personalized, weather-aware,
          and shareable for every trip.
        </motion.p>
        <Link
          to="/app"
          className="bg-gradient-to-r from-[#6B91B5] to-[#EE4A72] text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition"
        >
          Generate Your List →
        </Link>
      </section>

      {/* 2️⃣ Problem / Solution */}
      <section id="book">
        <HowItWorks />
      </section>

      {/* 3️⃣ Feature Highlights */}
      <section id="features" className="min-h-[80vh] flex flex-col items-center justify-center px-6 md:px-12 bg-white">
        <motion.h2
          className="text-3xl sm:text-4xl font-bold mb-12 text-center"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
        >
          All your trip essentials, simplified.
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl">
          {features.map((f, i) => (
            <motion.div
              key={i}
              className="p-6 rounded-2xl shadow-sm hover:shadow-md bg-[#f9fafb] text-center"
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              transition={{ delay: 0.1 * i }}
            >
              <h3 className="font-semibold text-lg text-[#6B91B5] mb-2">{f.title}</h3>
              <p className="text-gray-600 text-sm">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* === From the Blog Section === */}
      <section
        id="blog"
        className="min-h-[70vh] flex flex-col items-center justify-center bg-[#f9fafb] px-6 py-20 text-center"
      >
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">From the Blog</h2>
        <p className="text-gray-600 mb-12 max-w-xl">
          Insights, tips, and stories to help you travel smarter.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl w-full">
          {blogPosts.slice(0, 3).map((post, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all text-left flex flex-col justify-between"
            >
              {/* Thumbnail if available */}
              {post.image && (
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
              )}
              <div>
                <h3 className="text-xl font-semibold text-[#6B91B5] mb-2">
                  {post.title}
                </h3>
                <p className="text-gray-600 mb-4 text-sm line-clamp-3">
                  {post.description || post.excerpt}
                </p>
              </div>
              <Link
                to={`/blog/${post.slug}`}
                className="text-brandPink font-semibold hover:underline mt-auto"
              >
                Read More →
              </Link>
            </div>
          ))}
        </div>

        <Link
          to="/blog"
          className="mt-12 inline-block bg-gradient-to-r from-[#6B91B5] to-[#EE4A72] text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition"
        >
          View All Posts
        </Link>
      </section>

      {/* 5️⃣ Footer CTA */}
      <footer className="py-20 text-center bg-[#f9fafb]">
        <h3 className="pb-[20px] text-2xl sm:text-3xl font-bold mb-4">
          Ready to pack smarter?
        </h3>

        <Link
          to="/app"
          className="bg-gradient-to-r from-[#6B91B5] to-[#EE4A72] text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition"
        >
          Get Started →
        </Link>

        {/* Footer Navigation Links */}
        <div className="mt-10 text-sm text-gray-600">
          <nav className="flex justify-center flex-wrap gap-4 mb-2">
            <Link to="/about" className="hover:text-[#6B91B5] transition-colors">
              About
            </Link>
            <span>•</span>
            <Link to="/privacy" className="hover:text-[#6B91B5] transition-colors">
              Privacy
            </Link>
            <span>•</span>
            <Link to="/blog" className="hover:text-[#6B91B5] transition-colors">
              Blog
            </Link>
          </nav>
          <p>© {new Date().getFullYear()} SmartPackr. All rights reserved.</p>
        </div>
      </footer>

    </div>
  );
}
